import { padStart } from "lodash";
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";

import { getUnitDurationInCourse } from "@examspark/common";
import { ScrollToTop, useQueryGetCourseUnits } from "@examspark/common-app";
import { subjectMeta } from "@examspark/common-meta";
import {
  FlexBox,
  RouterLink,
  SelectInput,
  Text,
  Toolbar,
} from "@examspark/common-ui";
import {
  JumpBarContext,
  PilledJumpBar,
} from "@examspark/common-ui/dist/components/utils/JumpBar";
import { usePageScroll } from "@examspark/common-ui/dist/hooks/usePageScroll";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";
import { ParsedTCMPCUnit } from "@examspark/types";

import { AsideLayoutProvider } from "../../contexts/AsideLayoutContext";
import { serializeBrowseFilter } from "../browse/Browse";
import { WindowContext } from "../common/AppWrapper";
import Breadcrumbs from "../common/Breadcrumbs";
import ResourceTools from "../resources/ResourceTools";

import UnitPrimary from "./UnitPrimary";

const Styled = styled.main`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  position: relative;
`;

const Breadcrumb = styled.nav.attrs({ "aria-label": "Breadcrumb" })`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;

  border-bottom: 1px solid ${colorFromTheme("surface5")};

  background-color: white;
`;

const StyledStickyHeader = styled.div<{ opacity: number }>`
  position: fixed;
  top: 0;
  z-index: 2;

  width: calc(min(100% - 4rem, var(--app-contain-width)));

  transition: opacity 0.5s ease-in-out;
  opacity: ${(props) => props.opacity};
  // ensure that the header is still clickable
  // when the pills are not visible
  ${({ opacity }) =>
    opacity < 0.2 &&
    css`
      pointer-events: none;
    `}

  background-color: white;
  border-bottom: 2px solid ${colorFromTheme("surface5")};
`;

const Layout = styled.div<{ requestAside: boolean }>`
  padding-bottom: 75dvh;

  // if we have enough room, we scoot the contet over
  // when an aside is requested
  @media (min-width: 1024px) {
    ${({ requestAside }) =>
      requestAside &&
      css`
        display: grid;
        grid-template-columns: 1fr calc(48rem - max(50dvw - 50%, 0px));
        gap: 2rem;
      `}
  }

  // otherwise, we just float the aside fixed at the bottom of the screen
`;
const UnitAside = styled.aside`
  @media (max-width: 1024px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 999;
  }
`;

const TimeSpent = styled.div`
  font-size: 1.2rem;
  color: ${colorFromTheme("secondary", "textColor")};
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;

  gap: 1rem;

  width: 100%;
`;

const StyledBreadcrumbRow = styled(FlexBox)`
  flex-direction: row;
`;

const getUnitNumberLabel = (unit: ParsedTCMPCUnit) => {
  if (unit.numberLabel != null) return unit.numberLabel;
  const index = unit.course?.units?.findIndex((u) => u.id === unit.id);
  if (index != null) {
    return padStart((index + 1).toString(), unit.course?.units?.length, "0");
  }
  return "";
};

const breadcrumbs = ({
  unit,
  disableTab,
  sticky,
  mobile,
}: {
  unit: ParsedTCMPCUnit;
  disableTab?: true;
  sticky?: true;
  mobile?: boolean;
}) => [
  ...Breadcrumbs.Browse({ disableTab }).map((b) => !sticky && b),
  !sticky && (
    <RouterLink
      key="units"
      tabIndex={disableTab && -1}
      to={`/browse?filter=${serializeBrowseFilter({
        grade: [],
        type: ["UNIT"],
        subject: unit.subject?.[0],
      })}`}
    >
      IFDs
    </RouterLink>
  ),
  !sticky && !!unit.subject?.[0] && (
    <RouterLink
      key="subjects"
      tabIndex={disableTab && -1}
      to={`/browse?filter=${serializeBrowseFilter({
        grade: [],
        type: ["COURSE"],
        subject: unit.subject[0],
      })}`}
    >
      {subjectMeta[unit.subject[0] as keyof typeof subjectMeta].label}
    </RouterLink>
  ),
  unit.course?.id && unit.course?.name && (
    <RouterLink
      key="course"
      tabIndex={disableTab && -1}
      to={`/course/${unit.course?.id}`}
    >
      {unit.course?.name}
    </RouterLink>
  ),
  sticky && (
    <RouterLink to={"."}>
      {mobile ? `Unit ${getUnitNumberLabel(unit)}` : unit.name}
    </RouterLink>
  ),
];

const StickyHeader = ({ breadcrumbs }: { breadcrumbs: React.ReactNode[] }) => {
  const { y } = usePageScroll();

  return (
    <StyledStickyHeader opacity={y > 300 ? 1 : 0} aria-hidden="true">
      <StyledBreadcrumbRow style={{ padding: "1rem" }}>
        <Breadcrumbs items={breadcrumbs} />
      </StyledBreadcrumbRow>
      <PilledJumpBar wrap="nowrap" keepActiveInView isTabTarget={false} />
    </StyledStickyHeader>
  );
};

type UnitProps = {
  routerUnitId?: string;
  unit: ParsedTCMPCUnit;
};

const Unit = ({ unit, routerUnitId }: UnitProps) => {
  const win = useContext(WindowContext);
  const navigate = useNavigate();

  const [jumpBarEntries, setJumpBarEntries] = useState({});
  const [requestAside, setRequestAside] = useState<Record<string, boolean>>({});

  const { data: courseUnits } = useQueryGetCourseUnits(unit.courseId);
  const unitDuration = getUnitDurationInCourse(unit);

  return (
    <Styled>
      <ScrollToTop />
      <JumpBarContext.Provider
        value={{
          entries: jumpBarEntries,
          setEntries: setJumpBarEntries,
        }}
      >
        <StickyHeader
          breadcrumbs={breadcrumbs({
            unit,
            disableTab: true,
            sticky: true,
            mobile: win.device === "phone",
          })}
        />
        <Breadcrumb>
          <Breadcrumbs items={breadcrumbs({ unit })} />
        </Breadcrumb>
        <AsideLayoutProvider
          requestAside={requestAside}
          onRequestAside={(symbol, request) =>
            setRequestAside({ ...requestAside, [symbol]: request })
          }
          asidePortalId="unit-aside"
        >
          <Layout requestAside={Object.values(requestAside).some(Boolean)}>
            <UnitPrimary
              unit={unit}
              header={
                <>
                  <StyledHeader>
                    <Toolbar>
                      <SelectInput
                        value={routerUnitId}
                        options={
                          courseUnits?.map((u) => ({
                            label: u.name ?? "",
                            value: u.id ?? "",
                          })) ?? []
                        }
                        onChange={(value) => {
                          if (value == null) return;
                          if (value.value === routerUnitId) return;
                          navigate(`/unit/${value.value}`);
                        }}
                      />
                      <Toolbar.Right>
                        <ResourceTools resource={unit} allowAnnotations />
                      </Toolbar.Right>
                    </Toolbar>
                    <Text.H1
                      style={{ display: "inline-block", fontSize: "2.4rem" }}
                    >
                      {unit.name}
                    </Text.H1>
                    <TimeSpent>
                      Suggested Duration:&nbsp;
                      <span>{(+(unitDuration ?? 0)).toFixed(0)} days</span>
                    </TimeSpent>
                  </StyledHeader>
                </>
              }
            />
            <UnitAside id="unit-aside" />
          </Layout>
        </AsideLayoutProvider>
      </JumpBarContext.Provider>
    </Styled>
  );
};
export default Unit;
